import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {bindActionCreators} from "redux";
import {Button, Card, Icon, Form, Input, InputNumber, Select} from 'antd';
import {addProduct} from './actions';
import {formItemLayout} from "../utils/style";
import ResponsiveTable from "../common/ResponsiveTable";
import {getCategoryTree} from "../Category/actions";
import {getBrands} from "../Brand/actions";
import {getSuppliers} from "../Supplier/actions";
import SelectCategoryTree from "../Category/SelectCategoryTree";
import {v4 as uuidV4} from "../utils/uuid";
import axios from "axios";
import {authorizationHeaders} from "../utils/api";

const FormItem = Form.Item;
const ButtonGroup = Button.Group;
const Option = Select.Option;

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some(field => !!fieldsError[field]);
}

async function searchUpcOnGoogle(upc) {
  const ts = Date.now();
  const res = await axios.get(`${process.env.PUBLIC_URL}/api/v1/googleSearch/search?q=${encodeURIComponent(upc)}&_=${ts}`, {headers: authorizationHeaders()});
  const {status, data} = res;
  if (status !== 200) return '';
  const {results} = data;
  if (results.length < 1) return '';
  // eslint-disable-next-line no-useless-escape
  const regEx = new RegExp(`(upc|ean|${upc}| - |[.]{3})`, 'ig');
  return results[0].split('\n')[0]
    .replace(regEx, '')
    .trim();
}

const Statuses = {
  None: 0,
  Added: 1,
  Existing: 2,
  Error: 3,
};

function productsReducer(products, upc, index) {
  let product = products[upc];
  if (!product) {
    product = {
      status: Statuses.None,
      upc,
      categoryId: '',
      brandId: '',
      supplierId: '',
      name: '',
      supplierOrderCode: '',
      quantity: 0,
      productGuid: uuidV4()
    };
  }
  product.quantity += 1;
  products[upc] = product;
  return products;
}

// eslint-disable-next-line react/prop-types
const Step1 = ({categoryTree, defaultCategoryId, upcs, onSubmit, onCancel, getFieldDecorator, getFieldsError}) =>
  <Form onSubmit={onSubmit}>
    <FormItem
      {...formItemLayout}
      label="Default Category"
    >
      {getFieldDecorator('defaultCategoryId', {
        initialValue: defaultCategoryId,
      })(
        <SelectCategoryTree tree={categoryTree} hideNone />
      )}
    </FormItem>
    <FormItem
      {...formItemLayout}
      label="UPCs"
    >
      {getFieldDecorator('upcs', {
        rules: [
          {required: true, message: 'upcs are required'}
        ],
        initialValue: upcs,
      })(
        <Input.TextArea rows={33}/>
      )}
    </FormItem>
    <FormItem>
      <ButtonGroup>
        <Button
          type="primary"
          htmlType="submit"
          disabled={hasErrors(getFieldsError())}
        >
          Next
        </Button>
        <Button
          htmlType="button"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </ButtonGroup>
    </FormItem>
  </Form>;

const step2Columns = [
  {
    title: '',
    dataIndex: 'status',
    key: 'status',
  },
  {
    title: 'UPC',
    dataIndex: 'upc',
    key: 'upc'
  },
  {
    title: 'Category',
    dataIndex: 'categoryId',
    key: 'categoryId',
    width: 175,
  },
  {
    title: 'Brand',
    dataIndex: 'brandId',
    key: 'brandId',
    width: 175
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name'
  },
  {
    title: 'Supplier',
    dataIndex: 'supplierId',
    key: 'supplierId',
    width: 175
  },
  {
    title: 'Supplier Order Code',
    dataIndex: 'supplierOrderCode',
    key: 'supplierOrderCode'
  },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    key: 'quantity'
  },
];
// eslint-disable-next-line react/prop-types
const Status = ({value}) => {
  let type, color;
  switch (value) {
    case Statuses.Added:
      type = "check-circle";
      color = "#52c41a";
      break;
    case Statuses.Error:
      type = "close-circle";
      color = "#ff0000";
      break;
    case Statuses.Existing:
      type = "exclamation-circle";
      color = "#ff7700";
      break;
    default:
      return '';
  }
  return <Icon type={type}
               theme="twoTone"
               twoToneColor={color} />;
};
// eslint-disable-next-line react/prop-types
const Step2 = ({done, submitting, lookingUpProducts, products, categoryTree, brands, suppliers, onLookupProducts, onSubmit, onCancel, onDone, getFieldDecorator, getFieldsError}) => {
  const errors = getFieldsError();
  const columns = step2Columns.map(x => ({...x}));
  columns[0].render = (value) => <Status value={value}/>;
  columns[1].render = (value, _, index) => <Form.Item>
    {getFieldDecorator(`products_${index}_upc`, {
      initialValue: value,
    })(<Input readOnly/>)}
  </Form.Item>;
  columns[2].render = (value, _, index) => <Form.Item>
    {getFieldDecorator(`products_${index}_categoryId`, {
      rules: [{
        required: true,
        message: 'Category is required'
      }],
      initialValue: value
    })(
      <SelectCategoryTree tree={categoryTree} hideNone/>
    )}
  </Form.Item>;
  columns[3].render = (value, _, index) => <Form.Item>
    {getFieldDecorator(`products_${index}_brandId`, {
      initialValue: value
    })(
      <Select disabled={lookingUpProducts}>
        <Option key="addProduct-supplierId-null" value={null}>NONE</Option>
        {brands.map(brand =>
          <Option key={brand.brandId} value={brand.brandId}>{brand.name}</Option>
        )}
      </Select>
    )}
  </Form.Item>;
  columns[5].render = (value, _, index) => <Form.Item>
    {getFieldDecorator(`products_${index}_supplierId`, {
      initialValue: value
    })(
      <Select>
        <Option key="addProduct-supplierId-null" value={null}>NONE</Option>
        {suppliers.map(supplier =>
          <Option key={supplier.supplierId} value={supplier.supplierId}>{supplier.name}</Option>
        )}
      </Select>
    )}
  </Form.Item>;
  columns[4].render = (value, _, index) => <Form.Item>
    {getFieldDecorator(`products_${index}_name`, {
      rules: [{required: true, message: 'Name is required'}],
      initialValue: value
    })(<Input disabled={lookingUpProducts}/>)}
  </Form.Item>;
  columns[6].render = (value, _, index) => <Form.Item>
    {getFieldDecorator(`products_${index}_supplierOrderCode`, {
      initialValue: value
    })(<Input/>)}
  </Form.Item>;
  columns[7].render = (value, _, index) => <Form.Item>
    {getFieldDecorator(`products_${index}_quantity`, {
      rules: [{required: true, message: 'Quantity is required'}],
      initialValue: value
    })(<InputNumber/>)}
  </Form.Item>;
  return (
    <Form onSubmit={onSubmit}>
      <ResponsiveTable rowKey="upc" columns={columns} dataSource={products} pagination={false}/>
      <FormItem>
        {!done && <Button
          htmlType="button"
          disabled={submitting || lookingUpProducts}
          onClick={onLookupProducts}>
          Lookup Products
        </Button>}
        {!done && <ButtonGroup>
          <Button
            type="primary"
            htmlType="submit"
            disabled={submitting || lookingUpProducts || hasErrors(errors)}
          >
            Add Products
          </Button>
          <Button
            htmlType="button"
            disabled={submitting || lookingUpProducts}
            onClick={onCancel}
          >
            Back
          </Button>
        </ButtonGroup>}
        {done && <Button
          htmlType="button"
          onClick={onDone}
        >
          Done
        </Button>}
      </FormItem>
    </Form>
  );
};

class BulkAddProductForm extends Component {
  static propTypes = {
    form: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    salonId: PropTypes.string.isRequired,
    categoryTree: PropTypes.object.isRequired,
    brands: PropTypes.array.isRequired,
    suppliers: PropTypes.array.isRequired,
    addProduct: PropTypes.func.isRequired,
    getCategoryTree: PropTypes.func.isRequired,
    getBrands: PropTypes.func.isRequired,
    getSuppliers: PropTypes.func.isRequired,
  };

  state = {
    step: 0,
    upcs: '',
    defaultCategoryId: '',
    products: [],
    lookingUpProducts: false,
  }

  componentDidMount() {
    this.setState({step: parseInt(this.props.match.params.step || '1', 10)});
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.match.params.step !== prevProps.match.params.step) {
      this.setState({step: parseInt(this.props.match.params.step || '1', 10)});
    }
    const {salonId} = this.props;
    if (this.state.step !== prevState.step && this.state.step === 1) {
      this.props.getCategoryTree({salonId});
    }
    if (this.state.step !== prevState.step && this.state.step === 2) {
      this.props.getBrands({salonId});
      this.props.getSuppliers({salonId});
      this.props.getCategoryTree({salonId});
    }
  }

  onDone = () => {
    this.props.history.replace('/product/list');
  };

  goBack = () => {
    this.props.history.goBack();
  };

  lookupProducts = async() => {
    const {products} = this.state;
    const upcFields = products.map((x, i) => `products_${i}_upc`);
    const values = this.props.form.getFieldsValue(upcFields);
    this.setState({lookingUpProducts: true});
    try {
      for (let i = 0; i < products.length; i++) {
        const nameKey = `products_${i}_name`;
        const potentialName = values[nameKey] || await searchUpcOnGoogle(values[`products_${i}_upc`]);
        this.props.form.setFieldsValue({[nameKey]: potentialName});
      }
    } finally {
      this.setState({lookingUpProducts: false});
    }
  }

  handleSubmit1 = (e) => {
    e.preventDefault();
    this.props.form.validateFields((err, {upcs, defaultCategoryId}) => {
      if (err) return;
      const productsMap = upcs.split('\n').map(x => x.trim()).reduce(productsReducer, {});
      const products = Object.keys(productsMap).map(k => ({...productsMap[k], categoryId: defaultCategoryId}));
      this.setState({upcs, products});
      this.props.history.push('/product/bulk-add/2');
    });
  };

  handleSubmit2 = (e) => {
    e.preventDefault();
    const {salonId, addProduct} = this.props;
    const {products} = this.state;
    this.props.form.validateFields(async(err, values) => {
      if (err) return;
      let errors = 0;
      this.setState({bulkAdding: true});
      for (let i = 0; i < products.length; i++) {
        if ([Statuses.Added, Statuses.Existing].includes(products[i].status)) {
          continue;
        }
        const name = values[`products_${i}_name`];
        const addProductReq = {
          categoryId: values[`products_${i}_categoryId`],
          productGuid: products[i].productGuid,
          salonId,
          name,
          description: name,
          initialQuantity: values[`products_${i}_quantity`],
          salePrice: 0,
          brandId: values[`products_${i}_brandId`],
          packageQuantity: 1,
          unit: 'each',
          supplierId: values[`products_${i}_supplierId`],
          imageUri: '',
          supplierOrderCode: values[`products_${i}_supplierOrderCode`],
          limitValue: 0,
          initialSku: (products[i].upc || '').trim(),
          netCostPerUnit: 0,
          rejectSkuDuplicate: true,
        };
        let status;
        try {
          await addProduct(addProductReq);
          status = Statuses.Added;
        } catch (err) {
          const isExisting = err.message.includes('409');
          if (!isExisting) {
            errors++;
            console.error(err);
          }
          status = isExisting ? Statuses.Existing : Statuses.Error;
        } finally {
          this.setState(prev => ({
            products: prev.products.map((x, j) => (j === i ? {...x, status} : {...x}))
          }));
        }
      }
      this.setState({bulkAdding: false, done: errors === 0});
    });
  };

  render() {
    const {
      form: {getFieldDecorator, getFieldsError, resetFields},
      categoryTree, brands, suppliers,
    } = this.props;
    const {step, defaultCategoryId, upcs, products, lookingUpProducts, bulkAdding, done} = this.state;
    return (
      <Card title="Bulk Add Products">
        {step === 1 && <Step1
          categoryTree={categoryTree}
          defaultCategoryId={defaultCategoryId}
          upcs={upcs}
          onSubmit={this.handleSubmit1}
          onCancel={this.goBack}
          getFieldDecorator={getFieldDecorator}
          getFieldsError={getFieldsError}
        />}
        {step === 2 && <Step2
          done={done}
          submitting={bulkAdding}
          suppliers={suppliers}
          categoryTree={categoryTree}
          brands={brands}
          products={products}
          lookingUpProducts={lookingUpProducts}
          onLookupProducts={this.lookupProducts}
          onSubmit={this.handleSubmit2}
          onCancel={() => {
            resetFields();
            this.goBack();
          }}
          onDone={this.onDone}
          getFieldDecorator={getFieldDecorator}
          getFieldsError={getFieldsError}
        />}
      </Card>
    );
  }
}

const BulkAddProductPage = Form.create()(BulkAddProductForm);

const mapStateToProps = state => ({
  salonId: state.salon.selectedSalonId,
  categoryTree: state.category.categoryTree,
  brands: state.brand.brands,
  suppliers: state.supplier.suppliers,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  addProduct,
  getCategoryTree,
  getBrands,
  getSuppliers,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(BulkAddProductPage);



// WEBPACK FOOTER //
// ./src/Product/BulkAddProductPage.js